import { Breadcrumbs, Link, Pagination } from "@mui/material";
import Container from "components/container";
import EmptyState from "components/empty-state";
import Layout from "components/layout";
import NewsCard from "components/news-card";
import { ROUTES } from "constants/routes";
import { useInformationalContentList } from "hooks/api/use-informational-content-list";
import { useDictionary } from "hooks/use-dictionary";
import { useRequiredParams } from "hooks/use-required-params";
import { StaticContentSectionEnum } from "types/static-content";
import SectionTitle from "../home/components/section-title";

const isStaticContentSection = (section: string): section is StaticContentSectionEnum => {
  return Object.values(StaticContentSectionEnum).includes(section as StaticContentSectionEnum);
};

export const InformationalContentListPage = () => {
  const { section } = useRequiredParams<{ section: string }>();
  const dict = useDictionary();
  const sectionValue = isStaticContentSection(section) ? section : StaticContentSectionEnum.HEALTHCARE_SPECIALISTS;
  const { items, pages, page, isLoading, onPage } = useInformationalContentList(sectionValue);

  if (!isStaticContentSection(section)) {
    return null;
  }

  if (isLoading || !items) {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-8 py-12">
        <Container className="bg-theme-background" childClassName="space-y-4">
          <Breadcrumbs>
            <Link href="/">{dict.common.breadcrumbsRoot}</Link>
            <div>{dict.informationalContent.sections[section]}</div>
          </Breadcrumbs>
          <div className="space-y-5">
            <SectionTitle>{dict.informationalContent.sections[section]}</SectionTitle>
            <div className="grid md:grid-cols-2 gap-5">
              {items.map((item) => (
                <NewsCard
                  id={item.id}
                  shortSummary={item.shortSummary ?? ""}
                  title={item.title ?? ""}
                  publishAt={item.publishAt}
                  linkTo={ROUTES.informationalContent.articleView}
                  linkParamName="informationalContentArticleId"
                  key={item.id}
                />
              ))}
            </div>
            {items.length === 0 && (
              <div className="flex flex-col items-center bg-white border py-2">
                <EmptyState text={dict.informationalContent.noContent} />
              </div>
            )}
            <Pagination
              count={pages}
              page={page}
              color="primary"
              hideNextButton={pages === 1}
              hidePrevButton={pages === 1}
              onChange={(e, page) => onPage(page)}
            />
          </div>
        </Container>
      </div>
    </Layout>
  );
};
